<template>
    <ModalBase
        :name="'generar-enlace-pago-' + plazo.id"
        title="Generar enlace de pago"
        @close="close"
    >
        <div class="tw:space-y-4">
            <div class="tw:bg-blue-50 tw:border tw:border-blue-200 tw:rounded-lg tw:p-4 tw:space-y-2">
                <div class="tw:flex tw:justify-between">
                    <span class="tw:text-sm tw:font-medium tw:text-gray-700">Plazo Nº:</span>
                    <span class="tw:text-sm tw:text-gray-900 tw:font-semibold">{{ plazo.id }}</span>
                </div>
                <div class="tw:flex tw:justify-between">
                    <span class="tw:text-sm tw:font-medium tw:text-gray-700">Importe:</span>
                    <span class="tw:text-sm tw:text-gray-900 tw:font-semibold">{{ plazo.importe | formatear_precio }}</span>
                </div>
            </div>

            <div v-if="!generatedUrl && !generating">
                <p class="tw:text-sm tw:text-gray-600 tw:mb-4">
                    Genera un enlace de pago para que el deportista pueda abonar este plazo de forma online.
                </p>
                <div class="tw:flex tw:gap-2">
                    <Button variant="primary" @click="generarEnlace" :disabled="generating">
                        Generar enlace de pago
                    </Button>
                </div>
            </div>

            <div v-if="generating" class="tw:flex tw:items-center tw:justify-center tw:py-4">
                <p class="tw:text-sm tw:text-gray-500">Generando enlace...</p>
            </div>

            <div v-if="generatedUrl" class="tw:space-y-4">
                <div class="tw:bg-green-50 tw:border tw:border-green-200 tw:rounded-lg tw:p-4">
                    <p class="tw:text-sm tw:text-green-700 tw:font-medium tw:mb-2">Enlace generado correctamente</p>
                    <div class="tw:flex tw:gap-2">
                        <input
                            ref="urlInput"
                            :value="generatedUrl"
                            readonly
                            class="tw:flex-1 tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-lg tw:text-sm tw:bg-white tw:cursor-text"
                        />
                        <Button variant="border" @click="copiarEnlace">
                            <i class="fas fa-copy tw:mr-1"></i> Copiar
                        </Button>
                    </div>
                    <p v-if="copied" class="tw:text-xs tw:text-green-600 tw:mt-2">¡Enlace copiado al portapapeles!</p>
                </div>

                <div class="tw:border-t tw:border-gray-100 tw:pt-4">
                    <p class="tw:text-sm tw:text-gray-600 tw:mb-2">Enviar enlace por email (opcional):</p>
                    <div class="tw:flex tw:gap-2">
                        <BaseInputText
                            :value="email"
                            @input="email = $event"
                            name="email"
                            label="Email"
                        />
                        <Button variant="secondary" :disabled="!email || sending" @click="enviarEmail">
                            Enviar
                        </Button>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <Button variant="border" @click="close">Cerrar</Button>
        </template>
    </ModalBase>
</template>

<script setup>
    import { ref, getCurrentInstance } from 'vue'
    import { useToast } from '@/composables/common/useToast'
    import ModalBase from '@/modules/admin/ui/ModalBase.vue'
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue'
    import Button from '@/modules/admin/ui/Button.vue'

    const props = defineProps({
        plazo: {
            type: Object,
            required: true
        },
        onSubmit: {
            type: Function,
            required: true
        }
    })

    const emit = defineEmits(['close'])
    const instance = getCurrentInstance()
    const toast = useToast()

    const generating = ref(false)
    const generatedUrl = ref(null)
    const copied = ref(false)
    const email = ref('')
    const sending = ref(false)
    const urlInput = ref(null)

    const close = () => {
        instance.proxy.$modal.hide('generar-enlace-pago-' + props.plazo.id)
        emit('close')
    }

    const generarEnlace = async() => {
        generating.value = true
        try {
            const result = await props.onSubmit(props.plazo.id)
            if (result && result.url) {
                generatedUrl.value = result.url
            } else if (result && result.data && result.data.url) {
                generatedUrl.value = result.data.url
            } else if (typeof result === 'string') {
                generatedUrl.value = result
            }
        } catch {
            // Error handled by composable via toast
        } finally {
            generating.value = false
        }
    }

    const copiarEnlace = async() => {
        try {
            await navigator.clipboard.writeText(generatedUrl.value)
            copied.value = true
            setTimeout(() => { copied.value = false }, 2000)
        } catch {
            // Fallback: select input text
            if (urlInput.value) {
                urlInput.value.select()
                document.execCommand('copy')
                copied.value = true
                setTimeout(() => { copied.value = false }, 2000)
            }
        }
    }

    const enviarEmail = async() => {
        if (!email.value || sending.value) return
        sending.value = true
        try {
            // Email sending is handled by the backend through the API
            await props.onSubmit(props.plazo.id, email.value)
            toast.success('Enlace enviado por email correctamente')
        } catch {
            // Error handled by composable via toast
        } finally {
            sending.value = false
        }
    }
</script>
